/**
 * CourseProgress.jsx
 * Live progress bar for the MbuziPlus Course. It sits on the same scale as the
 * "% complete" bar on the course home: finished items out of all open items,
 * plus the part of the current lesson the student has scrolled through.
 * It follows the student's saved record, so it moves forward as they learn.
 */
import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../Libs/firebase-config.mjs";

import { GENERIC, itemsOf } from "./modules";

const KEYS = { "breed-selection": 14, anatomy: 12, ...Object.fromEntries(Object.values(GENERIC).map((m) => [m.id, itemsOf(m)])) };
const TOTAL = Object.values(KEYS).reduce((a, b) => a + b, 0);

const MILESTONES = [
  [100, "Course complete. Your exam is unlocked."],
  [75, "Final stretch. Keep going."],
  [50, "Halfway there."],
  [25, "A quarter done. Well done."],
  [0, "Good start. Every lesson counts."],
];

export default function CourseProgress({ user, isDark }) {
  const [done, setDone] = useState(0);
  const [frac, setFrac] = useState(0);
  const [inLesson, setInLesson] = useState(false);

  useEffect(() => {
    if (!user?.token) return undefined;
    const ref = doc(db, "imfuyo_academy", user.token, "mbuziplus_course", user.pid || user.token);
    return onSnapshot(ref, (snap) => {
      const d = snap.exists() ? snap.data() : {};
      setDone(Object.keys(KEYS).reduce(
        (n, k) => n + Math.min(KEYS[k], Object.values((d[k] && d[k].completed) || {}).filter(Boolean).length), 0));
    }, () => {});
  }, [user?.token]);

  useEffect(() => {
    const tick = () => {
      setInLesson(!!document.querySelector("[data-mbz-lesson]"));
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setFrac(max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0);
    };
    tick();
    window.addEventListener("scroll", tick, { passive: true });
    window.addEventListener("resize", tick);
    const t = setInterval(tick, 500);
    return () => { window.removeEventListener("scroll", tick); window.removeEventListener("resize", tick); clearInterval(t); };
  }, []);

  // A lesson counts as done when it is opened, so while one is open its own share grows as it is read.
  const live = inLesson && done > 0 ? Math.max(0, done - 1) + frac : done;
  const pct = Math.min(100, Math.round((live / TOTAL) * 100));
  const msg = MILESTONES.find(([m]) => pct >= m)[1];
  const bar = isDark ? "#1e3321" : "#dcebd6";

  return (
    <>
      <div role="progressbar" aria-label="MbuziPlus Course progress" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}
        style={{ position: "fixed", top: 0, left: 0, right: 0, height: 6, background: bar, zIndex: 3000 }}>
        <div style={{
          width: `${pct}%`, height: "100%", transition: "width .45s cubic-bezier(.2,.8,.2,1)",
          background: "linear-gradient(90deg,#285020,#3E7A32 55%,#8DC63F)", borderRadius: "0 4px 4px 0",
          boxShadow: "0 0 10px rgba(141,198,63,.65)",
        }} />
        {[25, 50, 75].map((m) => (
          <span key={m} style={{ position: "absolute", top: 0, left: `${m}%`, width: 2, height: "100%", background: pct >= m ? "rgba(255,255,255,.7)" : isDark ? "#0d1a0f" : "#fff" }} />
        ))}
      </div>
      <div style={{
        position: "fixed", left: "50%", transform: "translateX(-50%)", bottom: "max(14px, env(safe-area-inset-bottom, 0px))", zIndex: 3000,
        display: "flex", alignItems: "center", gap: 10, padding: "8px 16px 8px 8px", borderRadius: 999, maxWidth: "calc(100vw - 24px)",
        background: isDark ? "rgba(19,35,26,.94)" : "rgba(255,255,255,.96)", border: `1px solid ${isDark ? "#223328" : "#d8edd8"}`,
        boxShadow: "0 6px 24px rgba(20,60,20,.18)", backdropFilter: "blur(8px)", fontFamily: "inherit",
      }}>
        <span style={{
          minWidth: 46, height: 30, borderRadius: 999, display: "inline-flex", alignItems: "center", justifyContent: "center",
          background: "linear-gradient(135deg,#285020,#3E7A32)", color: "#fff", fontWeight: 800, fontSize: 13,
        }}>{pct}%</span>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: isDark ? "#cfe3d1" : "#285020", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{msg}</span>
      </div>
    </>
  );
}
